import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, X } from 'shared/assets/icons';
import * as ButtonStyled from 'shared/components/ui/custom-form-select/custom-form-select.style';
import { Size } from 'shared/components/ui/custom-form-select/types';
import { FormInput } from 'shared/components/ui/form-input';
import { Icon } from 'shared/components/ui/icon';
import { PopupMenu } from 'shared/components/ui/popup-menu';
import * as PopupMenuStyled from 'shared/components/ui/popup-menu/popup-menu.style';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { ID } from 'shared/types/id';

import { formatNumberRangeDisplay } from './format-number-range-display';
import { hasNumberRangeValue } from './has-number-range-value';

export interface FilterNumberRangeProps {
  id: ID;
  label: string;
  minLabel: string;
  maxLabel: string;
  placeholder: string;
  minValue?: number | null;
  maxValue?: number | null;
  onChange: (options: { id: ID; min?: number; max?: number }) => void;
  level?: number;
  compact?: boolean;
  icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  canClear?: boolean;
}

export const FilterNumberRange: React.FC<FilterNumberRangeProps> = ({
  id,
  label,
  minLabel,
  maxLabel,
  placeholder,
  minValue,
  maxValue,
  onChange,
  level,
  compact,
  icon,
  canClear,
}) => {
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [localMin, setLocalMin] = useState<string>('');
  const [localMax, setLocalMax] = useState<string>('');
  const [initialMin, setInitialMin] = useState<number | undefined>(undefined);
  const [initialMax, setInitialMax] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (menuOpen) {
      const min = minValue ?? undefined;
      const max = maxValue ?? undefined;
      setInitialMin(min);
      setInitialMax(max);
      setLocalMin(min?.toString() ?? '');
      setLocalMax(max?.toString() ?? '');
    }
  }, [menuOpen, minValue, maxValue]);

  const handleMinChange = (value: string) => {
    setLocalMin(value);
  };

  const handleMaxChange = (value: string) => {
    setLocalMax(value);
  };

  const handleClear = () => {
    onChange({
      id,
      min: undefined,
      max: undefined,
    });
    closeMenu();
  };

  const handleClose = () => {
    const numMin = localMin === '' ? undefined : parseFloat(localMin);
    const numMax = localMax === '' ? undefined : parseFloat(localMax);
    const finalMin = isNaN(numMin as number) ? undefined : numMin;
    const finalMax = isNaN(numMax as number) ? undefined : numMax;

    const hasChanged = finalMin !== initialMin || finalMax !== initialMax;

    if (hasChanged) {
      onChange({
        id,
        min: finalMin,
        max: finalMax,
      });
    }

    closeMenu();
  };

  const displayText = formatNumberRangeDisplay(placeholder, minValue, maxValue);
  const hasValue = hasNumberRangeValue(minValue, maxValue);
  const showClearButton = hasValue && canClear;

  return (
    <PopupMenuStyled.Select htmlFor={id} $level={level} $compact={!!compact}>
      {!!icon && (
        <PopupMenuStyled.AffixContainer $isSelected={false}>
          <PopupMenuStyled.IconContainer>
            <Icon icon={icon} />
          </PopupMenuStyled.IconContainer>
        </PopupMenuStyled.AffixContainer>
      )}
      <PopupMenuStyled.SelectContent>
        {label}
        <ButtonStyled.InputContainer ref={containerRef}>
          <ButtonStyled.Button
            $noBorders={false}
            $size={Size.ExtraSmall}
            $inheritColor={true}
            $isPlaceholder={!hasValue}
            onClick={openMenu}
            type="button"
            id={id}
          >
            {displayText}
          </ButtonStyled.Button>

          <ButtonStyled.InteractionContainer
            $inheritColor={true}
            $size={Size.ExtraSmall}
          >
            {showClearButton ? (
              <ButtonStyled.ClearButton
                onClick={handleClear}
                type="button"
                tabIndex={-1}
              >
                <Icon icon={X} />
              </ButtonStyled.ClearButton>
            ) : (
              <ButtonStyled.IconContainer onClick={openMenu}>
                <Icon icon={ChevronLeft} />
              </ButtonStyled.IconContainer>
            )}
          </ButtonStyled.InteractionContainer>

          {menuOpen && (
            <PopupMenu
              ref={menuRef}
              referenceElement={containerRef}
              onOutside={handleClose}
              position="bottom"
              fallbackPositions={[
                'top',
                'right-end',
                'right-start',
                'bottom-end',
                'bottom-start',
                'left-start',
                'left-end',
                'top-end',
                'top-start',
              ]}
              hasParentWidth
            >
              <FormInput
                label={minLabel}
                value={localMin}
                type="number"
                placeholder={placeholder}
                onChange={(e) => handleMinChange(e.target.value)}
                min={0}
                step={1}
                hideNumberButtons
              />
              <FormInput
                label={maxLabel}
                value={localMax}
                type="number"
                placeholder={placeholder}
                onChange={(e) => handleMaxChange(e.target.value)}
                min={0}
                step={1}
                hideNumberButtons
              />
            </PopupMenu>
          )}
        </ButtonStyled.InputContainer>
      </PopupMenuStyled.SelectContent>
    </PopupMenuStyled.Select>
  );
};
